import styled from 'styled-components';
import {
  AccountClient,
  apiResolver,
  GET_CLIENTS,
  GET_INVOICE_LIST,
  GET_INVOICE_PAYMENT_METHODS,
  GET_INVOICE_PAYMENT_TERMS,
  GET_INVOICE_STATUS,
  TicketClient
} from '@nile/graphql';
import { ALL_MODULES, getTranslatedText } from '@nile/components';
import { INVOICE_STATUS } from '@nile/components/src/Constants/index';
import { cloneDeep } from 'lodash';

const getAccessor = (col) => {
  switch (col.name) {
    case 'client':
      return 'client.name';
    case 'site':
      return 'site.name';
    case 'invoiceId':
      return 'displayId';
    case 'emailOperationalStatus':
      return 'status';
    case 'pushedToIntegratedAppStatus':
      return 'operation';
    default:
      return col.name;
  }
};

const getType = (col) => {
  if (col?.isCustom) {
    return 'CustomFieldCell';
  }
  switch (col.name) {
    case 'displayId':
    case 'invoiceId':
      return 'Link';
    case 'invoiceDate':
    case 'dueDate':
      return 'DateTime';
    case 'totalAmount':
    case 'amountDue':
      return 'Currency';
    case 'statusEnum':
      return 'Status';
    case 'emailOperationalStatus':
      return 'EmailStatus';
    case 'pushedToIntegratedAppStatus':
      return 'IntegrationStatus';
    default:
      return 'Text';
  }
};

const getCellType = (col) => {
  switch (col.name) {
    case 'displayId':
    case 'invoiceId':
      return 'Link';
    default:
      return 'Text';
  }
};

const isEditable = (col) => {
  switch (col.name) {
    default:
      return false;
  }
};

const isSortable = (col) => {
  switch (col.name) {
    case 'emailOperationalStatus':
    case 'pushedToIntegratedAppStatus':
      return false;
    default:
      return true;
  }
};

const isFilterable = (col) => {
  switch (col?.name) {
    case 'prioritizedId':
      return true;
    default:
      return false;
  }
};

const isBold = (col) => {
  switch (col?.name) {
    case 'client':
    case 'totalAmount':
      return true;
    default:
      return false;
  }
};

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'prioritizedId':
    case 'invoiceId':
      return 'displayId';
    default:
      return f.name;
  }
};

const getWidth = (col) => {
  switch (col.name) {
    case 'logoFileName':
      return 110;
    default:
      return 250;
  }
};

const getQueryVariables = (params) => {
  const listInfo = params?.listInfo;
  const variables = {
    listInfo: {
      value: listInfo,
      type: 'ListInfoInput',
      required: true
    }
  };
  return variables;
};

const getInvoiceFields = (personalizedColumns) => {
  let fieldsToRequest = [
    {
      listInfo: [
        'page',
        'pageSize',
        'hasMore',
        'totalCount',
        {
          sort: ['attribute', 'order']
        },
        'condition'
      ]
    }
  ];
  let allFields = [];

  allFields.push('invoiceId', 'displayId');

  allFields.push({
    client: [
      'accountId',
      'name',
      `customFields(fields: ${JSON.stringify(
        personalizedColumns.map((col) => col)
      )})`
    ]
  });

  allFields.push({
    site: ['name', 'id']
  });

  allFields.push(
    'customFields',
    'invoiceDate',
    'dueDate',
    'totalAmount',
    'statusEnum',
    'sentToClient',
    'sendEmailError',
    'emailOperationalStatus',
    'pushedToIntegratedAppStatus',
    'pushedToIntegratedAppError',
    'pushPaymentToIntegratedAppStatus',
    'pushPaymentToIntegratedAppError',
    'amountDue',
    'partiallyPaid'
  );

  allFields.push({
    invoicePaymentTerm: [
      'paymentTermId',
      'paymentTermName',
      'paymentTermValue',
      'placeholder',
      'systemPaymentTerm'
    ]
  });

  allFields.push({
    paymentMethod: ['paymentMethodId', 'paymentMethodName']
  });

  allFields.push(
    'paymentDate',
    'paymentReference',
    'paymentLinkToken',
    'paymentLink'
  );

  fieldsToRequest.push({
    invoices: allFields
  });
  return fieldsToRequest;
};

const getTicketFields = (personalizedColumns) => {
  let fields = personalizedColumns.filter((el) => !el.isCustom);
  let fieldsToRequest = [
    {
      listInfo: ['page', 'pageSize', 'hasMore', 'totalCount']
    }
  ];
  let allFields = [];
  fields.forEach((el) => {
    switch (el.name) {
      case 'client':
        allFields.push({
          client: ['name', 'accountId']
        });
        break;
      case 'requester':
        allFields.push({
          requester: ['name', 'firstName', 'lastName', 'userId']
        });
        break;
      case 'primaryContact':
        allFields.push({
          primaryContact: ['name', 'firstName', 'lastName', 'userId']
        });
        break;
      case 'secondaryContact':
        allFields.push({
          secondaryContact: ['name', 'firstName', 'lastName', 'userId']
        });
        break;
      case 'accountManager':
        allFields.push({
          accountManager: ['name', 'firstName', 'lastName', 'userId']
        });
        break;
      case 'stageStatus':
        allFields = allFields.concat(['stage', 'status']);
        break;
      default:
        allFields.push(el.name);
    }
  });
  if (personalizedColumns.length !== fields.length)
    allFields.push('customFields');
  allFields.push('accountId');
  fieldsToRequest.push({
    clients: allFields
  });
  return fieldsToRequest;
};

const dependencyMap = {
  subcause: 'cause',
  subcategory: 'category',
  technician: 'techGroup'
};

const isDependantField = (field) => {
  return Object.keys(dependencyMap).indexOf(field) > -1;
};

const getParentField = (child) => {
  return dependencyMap[child];
};

const getDependantField = (parent) => {
  return Object.keys(dependencyMap).find(
    (key) => dependencyMap[key] === parent
  );
};

const isFilterableCol = (col) => {
  switch (col?.name) {
    case 'client':
    case 'client.name':
    case 'site.name':
    case 'site':
    case 'statusEnum':
    case 'invoiceDate':
    case 'dueDate':
    case 'paymentDate':
    case 'paymentMethod.paymentMethodName':
    case 'invoicePaymentTerm.paymentTermName':
    case 'invoiceId':
    case 'displayId':
      return true;
    default:
      return false;
  }
};

const checkDynamicQuery = (f) => {
  switch (f.name) {
    case 'statusEnum':
    case 'client':
    case 'client.name':
    case 'paymentMethod.paymentMethodName':
    case 'invoicePaymentTerm.paymentTermName':
      return true;
    default:
      return false;
  }
};

const getDataType = (col) => {
  switch (col?.name) {
    case 'invoiceDate':
    case 'dueDate':
    case 'paymentDate':
      return 'daterange';
    // case 'displayId':
    case 'invoiceId':
      return 'Link';
    case 'totalAmount':
      return 'Currency';
    // case 'statusEnum':
    //   return 'statusEnum';
    default:
      return 'string';
  }
};

const getFilterType = (col) => {
  switch (col?.name) {
    case 'site':
    case 'site.name':
    case 'displayId':
    case 'invoiceId':
      return 'text';
    case 'invoiceDate':
    case 'dueDate':
    case 'paymentDate':
      return 'CustomTime';
    case 'client.name':
    case 'paymentMethod.paymentMethodName':
    case 'invoicePaymentTerm.paymentTermName':
      return 'select';
    case 'statusEnum':
      return 'statusEnum';
    default:
      return null;
  }
};

const getDynamicQuery = (f) => {
  const resolverModule = 'Ticket';
  let client = TicketClient;
  let query = apiResolver[resolverModule][f.name]?.api;
  const variables = apiResolver?.[resolverModule]?.[f?.name]?.variables || {};
  switch (f.name) {
    case 'client':
    case 'client.name':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      });
    case 'invoicePaymentTerm.paymentTermName':
      return AccountClient?.query({
        query: GET_INVOICE_PAYMENT_TERMS
      });
    case 'paymentMethod.paymentMethodName':
      return AccountClient?.query({
        query: GET_INVOICE_PAYMENT_METHODS
      });
    case 'statusEnum':
      return AccountClient?.query({
        query: GET_INVOICE_STATUS
      });
    default:
      query = apiResolver[resolverModule][f.name]?.api;
      client = apiResolver[resolverModule][f.name]?.client;
      return client.query({
        query: query,
        variables: variables
      });
  }
};

const generateOptions = (f, data, intl = {}) => {
  let allValues = [];

  switch (f.name) {
    case 'client':
    case 'client.name':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'statusEnum':
      allValues = data?.getInvoiceStatus?.map((el) => {
        return {
          ...el,
          name: getTranslatedText(intl, el),
          value: el,
          label: getTranslatedText(intl, el)
        };
      });
      break;

    case 'invoicePaymentTerm.paymentTermName':
      return data.getPaymentTerms.map((paymentTerm) => ({
        ...paymentTerm,
        label: paymentTerm?.paymentTermName,
        value: paymentTerm?.paymentTermName,
        name: paymentTerm?.paymentTermId
      }));
    case 'paymentMethod.paymentMethodName':
      return data.getPaymentMethods.map((paymentMethod) => ({
        ...paymentMethod,
        label: paymentMethod?.paymentMethodName,
        value: paymentMethod?.paymentMethodName,
        name: paymentMethod?.paymentMethodId
      }));
    default:
      allValues = [];
  }
  return allValues;
};

const getAttributeName = (col) => {
  switch (col?.name) {
    case 'invoiceId':
      return 'displayId';
    default:
      return col?.name;
  }
};

const getDefaultValue = (f, condition, intl = {}) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === getRuleAttribute(f);
  });

  const invoiceValues = Object.entries(INVOICE_STATUS).map((el) => {
    return {
      name: el[1].dropDownValue,
      value: el[1].value,
      label: el[1].dropDownValue
    };
  });

  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
      case 'daterange':
        return rule.value;
      default:
        break;
    }
    if (f.name !== 'statusEnum') {
      switch (f.filterType) {
        case 'text':
        case 'numeric':
        case 'decimal':
        case 'paragraph':
        case 'read_only':
          return rule.value;
        default:
          return rule.value.map((el) => {
            return {
              name: el,
              value: el,
              label: el
            };
          });
      }
    }
    if (f.name === 'statusEnum') {
      const selectedValues = invoiceValues.filter(
        (el, idx) => el.value === rule.value[idx]
      );
      rule.value = selectedValues;
      return rule.value;
    }
  }
  return '';
};

const getCustomAttributes = (col) => {
  if (col && col?.name) {
    switch (col.name) {
      case 'invoiceId':
        return 'displayId';
      default:
        return col.name;
    }
  }
};

export {
  getAccessor,
  isFilterableCol,
  getType,
  getCellType,
  getWidth,
  isSortable,
  isEditable,
  isFilterable,
  getRuleAttribute,
  getTicketFields,
  getInvoiceFields,
  getQueryVariables,
  dependencyMap,
  getDependantField,
  getParentField,
  isDependantField,
  isBold,
  checkDynamicQuery,
  getDataType,
  getFilterType,
  generateOptions,
  getDynamicQuery,
  getAttributeName,
  getDefaultValue,
  getCustomAttributes
};
